import React, { useEffect, useState } from 'react';
import { GoEye } from 'react-icons/go';
import { TailSpin } from 'react-loading-icons';
import { AddButton, RemoveButton } from '../content_management/reusableItems/buttons';
import { sendRequest, API_ROOT } from '../../supplementary_components/requests';
import { addNotification, addPopup } from '../../supplementary_components/store';
import { ConfirmationDialogue } from '../forms/ConfirmationDialogue';
import { CustomAddForm } from '../forms/CustomAddForm';
import { checkForEmptyField } from '../forms/Form';
import { getDropBoxFiles } from '../forms/input_fields/inputFieldDelegate';
import { getAttachmentEntityConfig } from './attachmentEntityConfig';
import styles from './supplementaryFilesListing.module.scss';

function attachmentMultipartHeaders() {
    // Omit Content-Type so the browser sets multipart boundary; companyId is injected at request time.
    return {};
}

export function normalizeAttachmentFiles(files) {
    if (!files) return [];
    const list = Array.isArray(files) ? files : [];
    return list.map((entry) => {
        if (typeof entry === 'string') {
            const trimmed = entry.trim();
            if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
                try {
                    return JSON.parse(trimmed);
                } catch (e) {
                    return { fileName: trimmed, fileType: trimmed, filePath: null };
                }
            }
            return { fileName: trimmed, fileType: trimmed, filePath: null };
        }
        if (entry && typeof entry === 'object') {
            return entry;
        }
        return { fileName: 'Unknown', fileType: 'Unknown', filePath: null };
    });
}

export function entityHasAttachments(files) {
    return normalizeAttachmentFiles(files).length > 0;
}

export const SupplementaryFilesListing = ({
    entityType,
    files,
    entityId,
    canEdit,
    onEntityUpdated,
}) => {
    const config = getAttachmentEntityConfig(entityType);
    const [fileList, setFileList] = useState(() => normalizeAttachmentFiles(files));
    const [loadingItemIndex, setLoadingItemIndex] = useState(null);

    useEffect(() => {
        setFileList(normalizeAttachmentFiles(files));
    }, [files]);

    const applyEntityResponse = (res) => {
        const updatedFiles = res?.[config.filesField];
        const normalized = normalizeAttachmentFiles(updatedFiles);
        setFileList(normalized);
        if (onEntityUpdated) {
            onEntityUpdated(res);
        }
    };

    return (
        <div className={styles.filesFrame}>
            {fileList.map((entry, index) => {
                const fileName = entry.fileName;
                return (
                    <div key={`${entry.filePath ?? fileName}-${index}`} className={styles.fileRow}>
                        <div className={styles.fileName}>{fileName}</div>
                        <div className={styles.fileType}>{entry.fileType}</div>
                        <div
                            className={styles.viewBtn}
                            onClick={() => {
                                if (!entry?.filePath) return;
                                setLoadingItemIndex(index);
                                const body = new FormData();
                                body.append('filePath', entry.filePath);
                                if (config.entityIdParam && entityId != null) {
                                    body.append(config.entityIdParam, entityId);
                                }
                                sendRequest(
                                    'POST',
                                    API_ROOT + config.getFileUrl,
                                    null,
                                    (res) => {
                                        res.blob().then((blob) => {
                                            const url = window.URL.createObjectURL(blob);
                                            window.open(url);
                                            setLoadingItemIndex(null);
                                        });
                                    },
                                    () => {
                                        addNotification({ message: 'Something went wrong', type: 'alert' });
                                        setLoadingItemIndex(null);
                                    },
                                    body,
                                    {},
                                    (b) => b,
                                    true,
                                );
                            }}
                        >
                            {loadingItemIndex === index ? (
                                <TailSpin
                                    stroke="var(--light-gray)"
                                    strokeWidth="0.2rem"
                                    width="1rem"
                                    height="1rem"
                                    speed={1.5}
                                />
                            ) : (
                                <GoEye />
                            )}
                        </div>
                        {canEdit ? (
                            <RemoveButton
                                action={() => {
                                    if (!entry?.filePath) return;
                                    addPopup({
                                        content: ConfirmationDialogue,
                                        popupData: {
                                            action: (postResultAction) => {
                                                const body = new FormData();
                                                body.append('filePath', entry.filePath);
                                                body.append(config.entityIdParam, entityId);
                                                sendRequest(
                                                    'POST',
                                                    API_ROOT + config.removeFileUrl,
                                                    null,
                                                    (res) => {
                                                        applyEntityResponse(res);
                                                        postResultAction(true);
                                                    },
                                                    () => {
                                                        postResultAction(false);
                                                        addNotification({
                                                            message: 'Something went wrong',
                                                            type: 'alert',
                                                        });
                                                    },
                                                    body,
                                                    attachmentMultipartHeaders(),
                                                    (b) => b,
                                                    false,
                                                );
                                            },
                                            name: 'Confirm',
                                            formTitle: 'Delete File',
                                            submitTitle: 'Confirm',
                                            method: 'POST',
                                            message: 'Are you sure you want to delete this file?',
                                        },
                                        postSuccessAction: () => {},
                                        EZCloseAllowed: true,
                                    });
                                }}
                            />
                        ) : null}
                    </div>
                );
            })}
            {canEdit ? (
                <div className={styles.addRow}>
                    <AddButton
                        customSize="1.6rem"
                        action={() => {
                            const inputFormData = {
                                columns: [
                                    {
                                        label: 'File',
                                        name: 'selectedFile',
                                        type: 'dropBox',
                                        fileType: 'any',
                                        multiple: true,
                                        mandatory: true,
                                        file: null,
                                        files: [],
                                    },
                                ],
                            };
                            addPopup({
                                content: CustomAddForm,
                                popupData: {
                                    action: (event, postResultAction) => {
                                        event.preventDefault();
                                        if (checkForEmptyField(inputFormData, event)) {
                                            return;
                                        }
                                        const dropColumn = inputFormData.columns.find(
                                            (clmn) => clmn.type === 'dropBox',
                                        );
                                        const selectedFiles = getDropBoxFiles(dropColumn);
                                        if (!selectedFiles.length) {
                                            return;
                                        }
                                        const uploadAt = (index, lastRes) => {
                                            if (index >= selectedFiles.length) {
                                                if (lastRes) {
                                                    applyEntityResponse(lastRes);
                                                }
                                                postResultAction(true);
                                                return;
                                            }
                                            const body = new FormData();
                                            body.append('file', selectedFiles[index]);
                                            body.append('fileName', selectedFiles[index].name);
                                            body.append(config.entityIdParam, entityId);
                                            body.append('fileType', config.uploadFileType);
                                            sendRequest(
                                                'POST',
                                                API_ROOT + config.addFileUrl,
                                                null,
                                                (res) => {
                                                    uploadAt(index + 1, res);
                                                },
                                                () => {
                                                    if (index > 0 && lastRes) {
                                                        applyEntityResponse(lastRes);
                                                    }
                                                    postResultAction(false);
                                                    addNotification({
                                                        message:
                                                            index > 0
                                                                ? `Uploaded ${index} of ${selectedFiles.length} files, then something went wrong`
                                                                : 'Something went wrong',
                                                        type: 'alert',
                                                    });
                                                },
                                                body,
                                                attachmentMultipartHeaders(),
                                                (b) => b,
                                                false,
                                            );
                                        };
                                        uploadAt(0, null);
                                    },
                                    name: 'Confirm',
                                    formTitle: 'Add File',
                                    submitTitle: 'Add',
                                    method: 'POST',
                                    columns: inputFormData.columns,
                                },
                                EZCloseAllowed: true,
                            });
                        }}
                    />
                </div>
            ) : null}
        </div>
    );
};
